"use client";

import { Cpu, Headphones, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  id: "innovation",
  title: "Flash-Frozen at Sea",
  description: "Our catch is processed and frozen within minutes of leaving the water, locking in peak nutrients and pristine ocean flavor for your table."
}, {
  id: "quality",
  title: "Cold-Chain Integrity",
  description: "We maintain a strict sub-zero environment from the fishing vessel to your doorstep, ensuring the highest standards of food safety and freshness."
}, {
  id: "support",
  title: "Village-Direct Delivery",
  description: "Exclusively serving Florida's premier village communities with white-glove service and local expertise that you can always trust."
}];
function FeatureCard({
  feature,
  index
}: {
  feature: typeof features[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const Icon = feature.id === "innovation" ? Cpu : feature.id === "quality" ? ShieldCheck : Headphones;
  return <div data-section-id="135771" data-index={index} ref={ref} className={`transition-all duration-700 transform h-full ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="group h-full border-border/40 bg-white/40 backdrop-blur-md hover:border-secondary/50 transition-all duration-300 hover:shadow-[0_0_40px_-15px_var(--color-secondary)]">
        <CardHeader>
          <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-secondary/10 text-primary transition-colors duration-300 group-hover:text-secondary group-hover:bg-secondary/20">
            <Icon className="size-7" />
          </div>
          <CardTitle className="text-2xl font-serif font-bold tracking-tight text-primary">{feature.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground font-sans text-lg leading-relaxed">
            {feature.description}
          </p>
        </CardContent>
      </Card>
    </div>;
}
export default function CoreTechValues() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section id="our-quality-promise" className="bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 overflow-hidden relative">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-secondary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold tracking-tight mb-6 text-primary">
            Our Quality <span className="text-secondary italic">Promise</span>
          </h2>
          <p className="text-muted-foreground font-sans text-lg md:text-xl">
            The principles of cold-chain excellence that drive us to deliver the ultimate Florida seafood experience.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, i) => <FeatureCard key={feature.id} feature={feature} index={i} />)}
        </div>
      </div>
    </section>;
}